import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { FlexRow, BasicContainer, PrimaryButton, BodyTextSmall, FlexCol, Loader } from '@minecraft.atoms';
import Input from '@minecraft.input';
import Pill from '@minecraft.pill';
import { useTranslation } from '../../I18N';
import { UsePayment } from '../types';
import { PaymentSummaryProps } from './types';

export const PromoCodeInput = ({
  onApplyCoupon,
  onRemoveCoupon,
  appliedCouponCode,
  applyCouponError,
  couponLoading = false,
}: {
  onApplyCoupon: UsePayment['applyCoupon'];
  onRemoveCoupon: PaymentSummaryProps['onRemoveCoupon'];
  appliedCouponCode?: string;
  applyCouponError?: PaymentSummaryProps['applyCouponError'];
  couponLoading?: PaymentSummaryProps['couponLoading'];
}) => {
  const { t } = useTranslation();
  const [promoCode, setPromoCode] = useState<string>('');
  const disabled = couponLoading || Boolean(appliedCouponCode);
  useEffect(() => {
    if (appliedCouponCode) {
      setPromoCode(appliedCouponCode);
    }
  }, [appliedCouponCode]);

  return (
    <Loader loading={couponLoading} LoaderComponent={<Skeleton containerClassName="cn_w-full" height="2rem" />}>
      <FlexRow w="full" gap="4" justifyContent="center" wrap="nowrap" data-testid="coupon-entry-container">
        <FlexCol w="full" gap="2">
          <Input
            name="promoCode"
            type="text"
            placeholder={t('common:paywall.summary.couponLabel')}
            disabled={disabled}
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value);
            }}
          />
          {appliedCouponCode && (
            <FlexCol gap="2">
              <BodyTextSmall color="disabled">{t('common:paywall.summary.onlyOneCouponAllowed')}</BodyTextSmall>
              <Pill
                text={appliedCouponCode}
                rightIcons={['close']}
                onClick={async () => {
                  await onRemoveCoupon();
                  setPromoCode('');
                }}
              />
            </FlexCol>
          )}
          {applyCouponError && (
            <BasicContainer>
              <BodyTextSmall color="error">{applyCouponError}</BodyTextSmall>
            </BasicContainer>
          )}
        </FlexCol>
        <PrimaryButton
          full={false}
          label={!appliedCouponCode ? t('common:paywall.summary.apply') : t('common:paywall.summary.applied')}
          variant={promoCode && !applyCouponError ? 'solid' : 'secondary'}
          onClick={() => {
            onApplyCoupon({ couponCode: promoCode });
          }}
          isSmall
          disabled={disabled || !promoCode}
        />
      </FlexRow>
    </Loader>
  );
};
